/**
 * test_flow tool - Guided flow testing with automatic diagnosis
 *
 * Provides a complete testing workflow:
 * 1. Gets flow details and expected input schema
 * 2. Prompts for test data if needed
 * 3. Runs the flow with waitForCompletion
 * 4. Reports success or diagnoses failure
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const testFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID to test"),
  testData: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Test input data to pass to the flow trigger"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  timeoutMs: z
    .number()
    .optional()
    .default(60000)
    .describe("Timeout in milliseconds (default: 60000)"),
});

export type TestFlowInput = z.infer<typeof testFlowInputSchema>;

export const testFlowTool = {
  name: "test_flow",
  description: `Test a Power Automate flow with guided feedback.

This tool:
1. Gets the flow's expected input schema (if any)
2. Runs the flow with provided test data
3. Waits for completion
4. Reports success or provides diagnosis for failures

Use this after creating or modifying a flow to verify it works correctly.`,
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID to test",
      },
      testData: {
        type: "object",
        description: "Test input data to pass to the flow trigger",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      timeoutMs: {
        type: "number",
        description: "Timeout in milliseconds (default: 60000)",
      },
    },
    required: ["flowId"],
  },
};

// Common error patterns and their fixes
const ERROR_PATTERNS: Array<{
  pattern: RegExp;
  category: string;
  fix: string;
}> = [
  {
    pattern: /ConnectionAuthorizationFailed|invalid.*connection|unauthorized/i,
    category: "Connection Error",
    fix: "Re-authenticate the connection at make.powerautomate.com > Data > Connections",
  },
  {
    pattern: /not found|404|does not exist/i,
    category: "Resource Not Found",
    fix: "Verify the resource path/ID is correct and the resource exists",
  },
  {
    pattern: /timeout|timed out|504/i,
    category: "Timeout",
    fix: "Increase timeout or break into smaller operations",
  },
  {
    pattern: /rate limit|throttl|429/i,
    category: "Rate Limited",
    fix: "Add delays between API calls or reduce request frequency",
  },
  {
    pattern: /expression.*invalid|syntax.*error/i,
    category: "Expression Error",
    fix: "Check expression syntax and verify all referenced fields exist",
  },
  {
    pattern: /permission|access denied|403/i,
    category: "Permission Error",
    fix: "Check service account permissions and API scopes",
  },
];

function diagnoseError(errorMessage: string): { category: string; fix: string } {
  for (const pattern of ERROR_PATTERNS) {
    if (pattern.pattern.test(errorMessage)) {
      return { category: pattern.category, fix: pattern.fix };
    }
  }
  return {
    category: "Unknown Error",
    fix: "Review the error message and check the flow definition",
  };
}

export async function handleTestFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = testFlowInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId }, "Testing flow");

  // Step 1: Get flow details
  let flow;
  try {
    flow = await api.getFlow(parsed.flowId, parsed.environment);
  } catch {
    return `Flow not found: ${parsed.flowId}. Use list_flows to see available flows.`;
  }

  const flowName = flow.properties.displayName;
  const definition = flow.properties.definition;

  // Step 2: Check trigger type and input schema
  const triggers = definition.triggers || {};
  const triggerName = Object.keys(triggers)[0];
  const trigger = triggerName ? triggers[triggerName] : undefined;

  let inputSchema: Record<string, unknown> | null = null;
  let triggerType = "unknown";

  if (trigger) {
    triggerType = trigger.type || "unknown";

    // Extract input schema for Request/Button triggers
    if (trigger.type === "Request" && trigger.inputs?.schema) {
      inputSchema = trigger.inputs.schema as Record<string, unknown>;
    }
  }

  // Step 3: Check if test data is needed but not provided
  if (inputSchema && !parsed.testData) {
    const properties = (inputSchema as { properties?: Record<string, unknown> }).properties || {};
    const required = (inputSchema as { required?: string[] }).required || [];

    if (Object.keys(properties).length > 0) {
      let output = `## Flow: ${flowName}\n\n`;
      output += `This flow expects input data. Please provide testData with these fields:\n\n`;
      output += `| Field | Type | Required |\n`;
      output += `|-------|------|----------|\n`;

      for (const [field, spec] of Object.entries(properties)) {
        const fieldSpec = spec as { type?: string };
        const isRequired = required.includes(field);
        output += `| ${field} | ${fieldSpec.type || "any"} | ${isRequired ? "Yes" : "No"} |\n`;
      }

      output += `\nExample:\n\`\`\`json\n`;
      const example: Record<string, string> = {};
      for (const field of Object.keys(properties)) {
        example[field] = `<${field} value>`;
      }
      output += JSON.stringify(example, null, 2);
      output += `\n\`\`\`\n`;
      output += `\nCall test_flow again with testData to run the test.`;

      return output;
    }
  }

  // Check flow state
  if (flow.properties.state !== "Started") {
    return `Cannot test "${flowName}" - flow is ${flow.properties.state}.\n\nUse toggle_flow to start it first.`;
  }

  // Step 4: Run the flow
  let output = `## Testing: ${flowName}\n\n`;
  output += `**Trigger type:** ${triggerType}\n`;
  output += `**Test data:** ${parsed.testData ? JSON.stringify(parsed.testData) : "None"}\n\n`;
  output += `Running flow...\n\n`;

  try {
    // Trigger the flow
    const triggerResponse = await api.triggerFlow(
      parsed.flowId,
      parsed.testData ? { body: parsed.testData } : undefined,
      parsed.environment
    );

    // Get the run ID - might be in name or id field
    let runId = triggerResponse?.name || (triggerResponse as unknown as { id?: string })?.id;

    // Poll for completion
    const startTime = Date.now();
    const timeout = parsed.timeoutMs ?? 60000;
    const pollInterval = 2000;

    // Fetch runs to find the triggered one.
    //
    // This used to fall back to `runs[0]` whenever the triggered run was not
    // found — including when the trigger HAD returned an id. Run history lags
    // indexing routinely, so the new run is often absent for a few seconds, and
    // the fallback then reported the PREVIOUS run's outcome under the new run's
    // id: a flow that just failed could be announced as "TEST PASSED" on the
    // strength of an unrelated earlier success. Only accept a run we can match.
    const runs = await api.getFlowRuns(parsed.flowId, parsed.environment, 5);
    let currentRun = runId ? runs.find((r) => r.name === runId) : runs[0];

    // If no run ID from trigger response, use the most recent run
    if (!runId && currentRun) {
      runId = currentRun.name;
    }

    if (!currentRun) {
      return runId
        ? `${output}⚠️ Flow triggered (run \`${runId}\`) but that run has not appeared in run history yet, so this test verified nothing.\n\nRun history lags by a few seconds. Check it with: get_runs flowId="${parsed.flowId}"\n`
        : `${output}⚠️ Flow triggered but no run found. Check get_runs for status.`;
    }

    while (
      currentRun &&
      Date.now() - startTime < timeout &&
      ["Running", "Waiting"].includes(currentRun?.properties?.status ?? "Running")
    ) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));

      const updatedRuns = await api.getFlowRuns(parsed.flowId, parsed.environment, 5);
      const updated = updatedRuns.find((r) => r.name === runId);
      if (updated) {
        currentRun = updated;
      } else {
        break;
      }
    }

    // Ensure we have a run ID for output
    runId = runId || currentRun?.name || "unknown";

    const finalStatus = currentRun?.properties?.status ?? "Unknown";
    const duration = currentRun?.properties?.endTime && currentRun?.properties?.startTime
      ? ((new Date(currentRun.properties.endTime).getTime() - new Date(currentRun.properties.startTime).getTime()) / 1000).toFixed(1)
      : null;

    // Step 5: Report results
    if (finalStatus === "Succeeded") {
      output += `✅ **TEST PASSED**\n\n`;
      output += `**Run ID:** ${runId}\n`;
      output += `**Duration:** ${duration ? `${duration}s` : "N/A"}\n\n`;
      output += `The flow executed successfully with the test input. That is one run, not a guarantee for other inputs.\n`;
    } else if (finalStatus === "Running" || finalStatus === "Waiting") {
      output += `⏳ **TEST IN PROGRESS**\n\n`;
      output += `**Run ID:** ${runId}\n`;
      output += `The flow is still running (timed out waiting).\n`;
      output += `Check status with: get_runs flowId="${parsed.flowId}"\n`;
    } else {
      // Failed
      output += `❌ **TEST FAILED**\n\n`;
      output += `**Run ID:** ${runId}\n`;
      output += `**Status:** ${finalStatus}\n\n`;

      const errorMsg = currentRun?.properties?.error?.message || "Unknown error";
      output += `**Error:** ${errorMsg}\n\n`;

      // Diagnose the error
      const diagnosis = diagnoseError(errorMsg);
      output += `**Category:** ${diagnosis.category}\n`;
      output += `**Suggested Fix:** ${diagnosis.fix}\n\n`;

      // Get action-level details if available
      try {
        const actions = await api.getFlowRunActions(parsed.flowId, runId, parsed.environment);
        const failedActions = actions.filter(a => a.properties.status === "Failed");

        if (failedActions.length > 0) {
          output += `**Failed Actions:**\n`;
          for (const action of failedActions) {
            output += `  • ${action.name}: ${action.properties.error?.message || "Unknown error"}\n`;
          }
          output += `\n`;
        }
      } catch {
        // Actions API might not be available
      }

      output += `**Next Steps:**\n`;
      output += `1. Fix the issue based on the suggestion above\n`;
      output += `2. Update the flow: update_flow flowId="${parsed.flowId}"\n`;
      output += `3. Test again: test_flow flowId="${parsed.flowId}"\n`;
    }

    return output;

  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);

    output += `❌ **TEST FAILED TO START**\n\n`;
    output += `**Error:** ${msg}\n\n`;

    const diagnosis = diagnoseError(msg);
    output += `**Category:** ${diagnosis.category}\n`;
    output += `**Suggested Fix:** ${diagnosis.fix}\n`;

    return output;
  }
}
